import React from 'react';

import { BRAND_DEPLOYMENT_STRIP } from '../../../../theme/tokens';

type Options = {
  /** The `overflow-x` scroll container (the ScrollStrip's Strip element). */
  containerRef: React.RefObject<HTMLDivElement>;
  /** Index (in the rendered card list) of the card to keep in view. */
  focalIndex: number;
  /** Rendered card count — re-follows when the strip grows (a step lands / done). */
  count: number;
  reduced: boolean;
  /** Off while the strip is collapsed (its container is unmounted). Toggling it back on
   *  re-attaches the listeners and places the strip instantly on the focal step. */
  enabled: boolean;
};

export type StripScroll = {
  /** Scrolled to the very start — the left chevron reads disabled. */
  atStart: boolean;
  /** Scrolled to the very end — the right chevron reads disabled. */
  atEnd: boolean;
  /** Chevron handlers: page the strip one card left / right (to the next snap centre). */
  prev: () => void;
  next: () => void;
};

const clamp = (n: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, n));
const maxScroll = (el: HTMLDivElement): number => Math.max(0, el.scrollWidth - el.clientWidth);

/**
 * Drives the horizontal deploy strip: it AUTO-FOLLOWS the focal (in-progress) card as the
 * deploy advances — but ONLY until the first manual scroll. Once the user drives (drag / wheel
 * / touch / the chevrons) they own the strip for good: it stays where they leave it and never
 * jumps back to the live step. Also reports `atStart`/`atEnd` for the chevrons.
 *
 * "User is scrolling" is detected from the container's own `scroll` event, gated by a
 * `programmatic` flag so the auto-follow / chevron scrolls never mistake themselves
 * for the user. That one signal covers every input — trackpad, wheel, touch momentum,
 * keyboard, and the mouse-drag below (which writes `scrollLeft` directly).
 *
 * All motion is native scroll (no GSAP): auto-follow + chevrons use smooth `scrollTo`; mouse
 * drag sets `scrollLeft` directly (1:1). When the user's scroll settles it eases to the nearest
 * card centre (JS snap — NOT native CSS scroll-snap, whose momentum yank-back reads as a
 * bounce). Under reduced motion every programmatic scroll is instant. Touch uses the browser's
 * own momentum scroll. The per-frame card choreography off this scroll lives in
 * {@link useStripChoreography}, not here.
 */
export const useStripScroll = ({ containerRef, focalIndex, count, reduced, enabled }: Options): StripScroll => {
  const [atStart, setAtStart] = React.useState(true);
  const [atEnd, setAtEnd] = React.useState(false);

  const interactingRef = React.useRef(false);
  const draggingRef = React.useRef(false);
  const programmaticRef = React.useRef(false);
  const programmaticTimerRef = React.useRef<number | undefined>(undefined);
  const snapTimerRef = React.useRef<number | undefined>(undefined);
  const rafScrollRef = React.useRef<number | undefined>(undefined);
  const lastSetRef = React.useRef<number | null>(null);
  const firstRunRef = React.useRef(true);
  const prevEnabledRef = React.useRef(false);

  // Kept fresh so the event/timer callbacks read the latest values without re-binding.
  const focalRef = React.useRef(focalIndex);
  focalRef.current = focalIndex;
  const reducedRef = React.useRef(reduced);
  reducedRef.current = reduced;

  const updateEdges = React.useCallback((): void => {
    const el = containerRef.current;
    if (!el) return;
    const max = maxScroll(el);
    setAtStart(el.scrollLeft <= 1);
    setAtEnd(el.scrollLeft >= max - 1);
  }, [containerRef]);

  // The card whose centre is nearest the viewport centre — the anchor the chevrons page from.
  const nearestIndex = React.useCallback((): number => {
    const el = containerRef.current;
    if (!el) return 0;
    const centre = el.scrollLeft + el.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < el.children.length; i += 1) {
      const child = el.children[i];
      if (!(child instanceof HTMLElement)) continue;
      const dist = Math.abs(child.offsetLeft + child.offsetWidth / 2 - centre);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }
    return best;
  }, [containerRef]);

  // Flag the next burst of scroll events as ours, so the `scroll` listener doesn't read an
  // auto-follow / chevron / bullet scroll as the user taking over. Cleared after it settles.
  const beginProgrammatic = React.useCallback((): void => {
    programmaticRef.current = true;
    if (programmaticTimerRef.current !== undefined) window.clearTimeout(programmaticTimerRef.current);
    programmaticTimerRef.current = window.setTimeout(() => {
      programmaticRef.current = false;
    }, BRAND_DEPLOYMENT_STRIP.PROGRAMMATIC_MS);
  }, []);

  // Cancel any in-flight custom snap animation (a fresh interaction / scroll supersedes it).
  const stopAnimatedScroll = React.useCallback((): void => {
    if (rafScrollRef.current !== undefined) {
      window.cancelAnimationFrame(rafScrollRef.current);
      rafScrollRef.current = undefined;
    }
    lastSetRef.current = null;
  }, []);

  // Animate scrollLeft to a target with a PRONOUNCED ease-out (easeOutQuart): the card visibly
  // decelerates into its centred rest — the premium settle the browser's own smooth scroll
  // can't express. Aborts the instant the user grabs the scroll (the position drifts from the
  // value WE last wrote), handing control straight back.
  const animateScrollTo = React.useCallback(
    (to: number): void => {
      const el = containerRef.current;
      if (!el) return;
      stopAnimatedScroll();
      const from = el.scrollLeft;
      const dist = to - from;
      if (Math.abs(dist) < 1) return;
      const easeOut = (t: number): number => 1 - Math.pow(1 - t, 4);
      let startTime: number | null = null;
      const step = (now: number): void => {
        const node = containerRef.current;
        if (!node) {
          stopAnimatedScroll();
          return;
        }
        // Position drifted from what WE last set → the user grabbed the scroll; bail to them.
        if (lastSetRef.current !== null && Math.abs(node.scrollLeft - lastSetRef.current) > 3) {
          stopAnimatedScroll();
          return;
        }
        if (startTime === null) startTime = now;
        const t = Math.min(1, (now - startTime) / BRAND_DEPLOYMENT_STRIP.SNAP_MS);
        node.scrollLeft = from + dist * easeOut(t);
        lastSetRef.current = node.scrollLeft; // read back the applied (maybe rounded) value
        if (t < 1) {
          rafScrollRef.current = window.requestAnimationFrame(step);
        } else {
          rafScrollRef.current = undefined;
          lastSetRef.current = null;
        }
      };
      rafScrollRef.current = window.requestAnimationFrame(step);
    },
    [containerRef, stopAnimatedScroll],
  );

  const scrollToIndex = React.useCallback(
    (index: number, smooth: boolean): void => {
      const el = containerRef.current;
      if (!el) return;
      const child = el.children[index] as HTMLElement | undefined;
      if (!child) return;
      const target = clamp(child.offsetLeft - (el.clientWidth - child.offsetWidth) / 2, 0, maxScroll(el));
      if (Math.abs(el.scrollLeft - target) < 1) return; // already centred
      if (smooth && !reducedRef.current) {
        animateScrollTo(target);
        return;
      }
      // Instant path (first-mount placement + reduced motion): flag it so the scroll listener
      // doesn't read the jump as the user driving.
      stopAnimatedScroll();
      beginProgrammatic();
      el.scrollTo({ left: target, behavior: 'instant' });
    },
    [containerRef, animateScrollTo, stopAnimatedScroll, beginProgrammatic],
  );

  const scrollToFocal = React.useCallback((smooth: boolean): void => scrollToIndex(focalRef.current, smooth), [scrollToIndex]);

  // The user driving the strip (drag / wheel / chevron) takes it over FOR GOOD: auto-follow of
  // the live step stops, so the strip stays exactly where they leave it and never jumps back to
  // the in-progress card. Auto-follow therefore only runs BEFORE the first interaction (the
  // initial placement + live tracking of the deploy).
  const markInteracting = React.useCallback((): void => {
    interactingRef.current = true;
  }, []);

  // Ease the settled scroll to the nearest card's centre — a single monotonic smooth scroll
  // (JS, not native CSS scroll-snap), so momentum never overshoots a snap point and springs
  // back. A no-op when we're already centred.
  const snapToNearest = React.useCallback((): void => {
    if (snapTimerRef.current !== undefined) {
      window.clearTimeout(snapTimerRef.current);
      snapTimerRef.current = undefined;
    }
    scrollToIndex(nearestIndex(), true);
  }, [scrollToIndex, nearestIndex]);

  // Debounced off the scroll signal: fire the snap once the user's scroll (wheel / trackpad /
  // keyboard momentum) has actually gone quiet for a beat. A held drag snaps on release instead.
  const scheduleSnap = React.useCallback((): void => {
    if (snapTimerRef.current !== undefined) window.clearTimeout(snapTimerRef.current);
    snapTimerRef.current = window.setTimeout(() => {
      snapTimerRef.current = undefined;
      if (draggingRef.current) return;
      snapToNearest();
    }, BRAND_DEPLOYMENT_STRIP.SNAP_IDLE_MS);
  }, [snapToNearest]);

  // Auto-follow the focal card on advance (instant on first mount, then the pronounced ease) —
  // unless the user has taken the strip over. Layout effect so offsets are measured after DOM
  // update.
  React.useLayoutEffect(() => {
    // Collapsed: the strip is unmounted, nothing to follow. Reset so the next expand places the
    // strip INSTANTLY on the focal step (no long pan across the whole timeline).
    if (!enabled) {
      prevEnabledRef.current = false;
      return;
    }
    // Refresh the chevron edges on EVERY count/focal change, even once the user has taken over:
    // auto-follow is suppressed then, but the strip may have grown (the confirm card is appended
    // once done), so atEnd must still recompute or the right chevron stays wrongly disabled.
    updateEdges();
    const reExpanded = !prevEnabledRef.current;
    prevEnabledRef.current = true;
    if (interactingRef.current) return;
    const smooth = !firstRunRef.current && !reExpanded;
    firstRunRef.current = false;
    scrollToFocal(smooth);
  }, [focalIndex, count, scrollToFocal, updateEdges, enabled]);

  // Edge + active-card tracking, plus the single "user is scrolling" signal.
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el || !enabled) return undefined;
    const onScroll = (): void => {
      updateEdges();
      // Our own scroll (an instant jump, or a frame of the custom snap animation) must not read
      // as the user driving.
      const ours = programmaticRef.current || rafScrollRef.current !== undefined;
      if (!ours) {
        markInteracting();
        // Wheel / trackpad / keyboard: ease to the nearest centre once the scroll settles. A
        // held drag is excluded — it snaps from endDrag on release.
        if (!draggingRef.current) scheduleSnap();
      }
    };
    // `scrollend` fires when scrolling fully stops (native; both user + programmatic). It
    // ends the "programmatic" window at the smooth-scroll's actual completion, so a scroll
    // that outlasts the PROGRAMMATIC_MS safety timeout can't be misread as the user driving.
    const onScrollEnd = (): void => {
      programmaticRef.current = false;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    el.addEventListener('scrollend', onScrollEnd, { passive: true });
    updateEdges();
    return () => {
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('scrollend', onScrollEnd);
    };
  }, [containerRef, updateEdges, markInteracting, scheduleSnap, enabled]);

  // Mouse drag-to-scroll. Mouse only (touch uses native momentum scroll); skips presses
  // that land on an interactive control so the confirmation card's buttons still click.
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el || !enabled) return undefined;
    let startX = 0;
    let startScroll = 0;

    const endDrag = (): void => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
      markInteracting(); // arm the resume countdown from the moment the drag is released
      snapToNearest(); // ease to the nearest card centre now the drag is released
    };
    const onPointerMove = (e: PointerEvent): void => {
      if (!draggingRef.current) return;
      if (e.buttons === 0) {
        // Button was released outside the window — no pointerup reached us; recover.
        endDrag();
        return;
      }
      el.scrollLeft = startScroll - (e.clientX - startX); // fires `scroll` → marks interacting
    };
    const onPointerDown = (e: PointerEvent): void => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      if ((e.target as HTMLElement).closest('button, a')) return;
      draggingRef.current = true;
      stopAnimatedScroll(); // a grab cancels any in-flight snap ease
      startX = e.clientX;
      startScroll = el.scrollLeft;
      markInteracting();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    };

    el.addEventListener('pointerdown', onPointerDown);
    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
    };
  }, [containerRef, markInteracting, snapToNearest, stopAnimatedScroll, enabled]);

  // Page one card toward either end: ease to the neighbour of whatever card is centred now,
  // landing EXACTLY on its centre. Anchoring on the nearest card (not a relative scrollBy)
  // means a click always resolves to a clean centre — no residual re-settle — so paging reads
  // smooth even mid-drift.
  const page = React.useCallback(
    (dir: 1 | -1): void => {
      const el = containerRef.current;
      if (!el) return;
      const last = el.children.length - 1;
      markInteracting();
      scrollToIndex(clamp(nearestIndex() + dir, 0, last), true);
    },
    [containerRef, markInteracting, nearestIndex, scrollToIndex],
  );

  const prev = React.useCallback(() => page(-1), [page]);
  const next = React.useCallback(() => page(1), [page]);
  React.useEffect(
    () => () => {
      if (programmaticTimerRef.current !== undefined) window.clearTimeout(programmaticTimerRef.current);
      if (snapTimerRef.current !== undefined) window.clearTimeout(snapTimerRef.current);
      if (rafScrollRef.current !== undefined) window.cancelAnimationFrame(rafScrollRef.current);
    },
    [],
  );

  return { atStart, atEnd, prev, next };
};

export default useStripScroll;
